import type { SurveyOverallScores } from '@engagement-web-app/common';
import {
    CategoryScale,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { formatDateToLabel, formatNumber } from '@/utils';
import { customCanvasBackgroundColorOptions, customCanvasBackgroundColorPlugin } from '@/utils/chart';
import * as sharedStyles from '../styles.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

type DiAverageChartPerYearProps = {
    /** `null` while still loading. */
    scores: SurveyOverallScores[] | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function DiAverageChartPerYear({ scores, isError, onRetry }: DiAverageChartPerYearProps) {
    if (isError) {
        return <ErrorOverlay active onRetry={onRetry} message="DI 平均 - 複数年比較の取得に失敗しました。" />;
    }

    if (!scores) return <DiAverageChartPerYearSkeleton />;

    return <DiAverageChartPerYearContent scores={scores} />;
}

type DiAverageChartPerYearContentProps = {
    [K in keyof Pick<DiAverageChartPerYearProps, 'scores'>]: NonNullable<DiAverageChartPerYearProps[K]>;
};

// Split from the buffer above so its hooks are never called conditionally.
function DiAverageChartPerYearContent({ scores }: DiAverageChartPerYearContentProps) {
    const [span, setSpan] = useState(scores.length);

    const data = useMemo(() => {
        const chronologicalScores = scores.slice(0, span).reverse();

        return {
            labels: chronologicalScores.map((s) => formatDateToLabel(s.surveyDate)),
            datasets: [
                {
                    label: 'DI平均',
                    data: chronologicalScores.map((s) => s.di),
                    borderColor: '#3b82f6',
                    backgroundColor: '#3b82f6',
                    pointStyle: 'circle',
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    tension: 0.4,
                },
            ],
        };
    }, [scores, span]);

    const options = useMemo<ChartOptions<'line'>>(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    min: 0,
                    max: 100,
                    ticks: { stepSize: 25 },
                    grid: { color: '#e5e7eb' },
                },
                x: {
                    grid: { color: '#e5e7eb' },
                },
            },
            plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                    callbacks: {
                        title: (items) => items[0].label,
                        label: (item) => `DI平均：${item.parsed.y != null ? formatNumber(item.parsed.y) : 'N/A'}`,
                    },
                },
                ...customCanvasBackgroundColorOptions,
            },
        };
    }, []);

    return (
        <Section flex>
            <SectionHeader
                title="DI 平均 - 複数年比較"
                description="組織全体の DI 平均を時系列で並べ、表示年数を切り替えて推移を確認できます。"
            />

            <Flex direction="column" gap="6" align="start">
                {scores.length >= 2 && (
                    <Select value={span} onChange={(e) => setSpan(Number(e.target.value))}>
                        {scores
                            .slice(1)
                            .map((_, i) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: Needed to count the number of years
                                <option key={i + 2} value={i + 2}>
                                    {i + 2}年
                                </option>
                            ))
                            .reverse()}
                    </Select>
                )}

                <div className={sharedStyles.chartContainer}>
                    <Line data={data} options={options} plugins={[customCanvasBackgroundColorPlugin]} />
                </div>
            </Flex>
        </Section>
    );
}

export function DiAverageChartPerYearSkeleton() {
    return (
        <Section flex>
            <Flex direction="column" gap="6" align="start">
                <Flex direction="column" gap="2" fullWidth>
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Flex gap="2" align="center">
                    <Skeleton.Box width={120} height={42} />
                    <Skeleton.Box width={120} height={42} />
                </Flex>

                <Skeleton.Box width="100%" height={300} />
            </Flex>
        </Section>
    );
}
